/**
 * Impersonate API Service
 *
 * Serviço para funcionalidade "Acessar Como" (impersonate user).
 * Permite admins acessarem o sistema como outro usuário.
 *
 * @since 2025-01-10
 */

import { api, getJwtToken, getRefreshToken } from './apiClient';
import type { AuthUser } from './authApi';

// ==========================================
// TYPES
// ==========================================

export interface ImpersonateUserInfo {
  id: number;
  login: string;
  name: string;
  email?: string;
  roles: string[];
  /** Role principal (menor order_hierarquical). Pode ser null/undefined se sem roles. */
  primaryRole?: string | null;
}

export interface ImpersonateStartRequest {
  targetPersonId: number;
}

export interface ImpersonateStartResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  impersonatedUser: ImpersonateUserInfo;
  originalUser: ImpersonateUserInfo;
  message: string;
}

export interface ImpersonateExitResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: ImpersonateUserInfo;
  message: string;
}

export interface ImpersonateInfo {
  isImpersonating: boolean;
  originalUserId?: number;
  originalUsername?: string;
  originalName?: string;
  impersonatedUserId?: number;
  impersonatedUsername?: string;
  impersonatedName?: string;
  /** Role principal do usuário impersonado (menor order_hierarquical). */
  impersonatedPrimaryRole?: string | null;
  startedAt?: string;
}

// Keys para localStorage
const STORAGE_KEYS = {
  ORIGINAL_TOKEN: 'tmf_original_token',
  ORIGINAL_REFRESH_TOKEN: 'tmf_original_refresh_token',
  ORIGINAL_USER: 'tmf_original_user',
  IMPERSONATE_DATA: 'tmf_impersonate_data',
};

// ==========================================
// API FUNCTIONS
// ==========================================

/**
 * Inicia impersonate como outro usuário
 */
export async function startImpersonate(
  targetPersonId: number
): Promise<ImpersonateStartResponse> {
  const endpoint = '/core/auth/impersonate/start';

  // Salvar tokens originais antes do impersonate (com validação)
  const currentToken = getJwtToken();
  const currentRefreshToken = getRefreshToken();
  const currentUser = localStorage.getItem('tmf_user');

  if (currentToken) {
    localStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, currentToken);
  }
  if (currentRefreshToken) {
    localStorage.setItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN, currentRefreshToken);
  }
  if (currentUser) {
    localStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, currentUser);
  }

  const response = await api.post<{
    success: boolean;
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    impersonated_user: {
      id: number;
      login: string;
      name: string;
      email?: string;
      roles: string[];
      primary_role?: string | null;
    };
    original_user: {
      id: number;
      login: string;
      name: string;
      email?: string;
      roles: string[];
      primary_role?: string | null;
    };
    message: string;
  }>(endpoint, {
    target_person_id: targetPersonId,
  });

  // Atualizar tokens com os do impersonate
  if (response.access_token) {
    localStorage.setItem('tmf_token', response.access_token);
    localStorage.setItem('tmf_refresh_token', response.refresh_token);
  }

  // Salvar dados do impersonate
  const impersonateData: ImpersonateInfo = {
    isImpersonating: true,
    originalUserId: response.original_user.id,
    originalUsername: response.original_user.login,
    originalName: response.original_user.name,
    impersonatedUserId: response.impersonated_user.id,
    impersonatedUsername: response.impersonated_user.login,
    impersonatedName: response.impersonated_user.name,
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEYS.IMPERSONATE_DATA, JSON.stringify(impersonateData));

  return {
    success: response.success,
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    tokenType: response.token_type,
    expiresIn: response.expires_in,
    impersonatedUser: {
      id: response.impersonated_user.id,
      login: response.impersonated_user.login,
      name: response.impersonated_user.name,
      email: response.impersonated_user.email,
      roles: response.impersonated_user.roles,
      primaryRole: response.impersonated_user.primary_role ?? null,
    },
    originalUser: {
      id: response.original_user.id,
      login: response.original_user.login,
      name: response.original_user.name,
      email: response.original_user.email,
      roles: response.original_user.roles,
      primaryRole: response.original_user.primary_role ?? null,
    },
    message: response.message,
  };
}

/**
 * Sai do modo impersonate e volta ao usuário original
 */
export async function exitImpersonate(): Promise<ImpersonateExitResponse> {
  const endpoint = '/core/auth/impersonate/exit';

  const response = await api.post<{
    success: boolean;
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: {
      id: number;
      login: string;
      name: string;
      email?: string;
      roles: string[];
      primary_role?: string | null;
    };
    message: string;
  }>(endpoint, {});

  // Restaurar tokens originais
  if (response.access_token) {
    localStorage.setItem('tmf_token', response.access_token);
    localStorage.setItem('tmf_refresh_token', response.refresh_token);
  }

  // Limpar dados de impersonate
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
  localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_DATA);

  return {
    success: response.success,
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    tokenType: response.token_type,
    expiresIn: response.expires_in,
    user: {
      id: response.user.id,
      login: response.user.login,
      name: response.user.name,
      email: response.user.email,
      roles: response.user.roles,
      primaryRole: response.user.primary_role ?? null,
    },
    message: response.message,
  };
}

/**
 * Obtém informações do impersonate atual
 */
export async function getImpersonateInfo(): Promise<ImpersonateInfo> {
  const endpoint = '/core/auth/impersonate/info';

  try {
    const response = await api.get<{
      is_impersonating: boolean;
      original_user_id?: number;
      original_username?: string;
      original_name?: string;
      impersonated_user_id?: number;
      impersonated_username?: string;
      impersonated_name?: string;
      impersonated_primary_role?: string | null;
      started_at?: string;
    }>(endpoint);

    return {
      isImpersonating: response.is_impersonating,
      originalUserId: response.original_user_id,
      originalUsername: response.original_username,
      originalName: response.original_name,
      impersonatedUserId: response.impersonated_user_id,
      impersonatedUsername: response.impersonated_username,
      impersonatedName: response.impersonated_name,
      impersonatedPrimaryRole: response.impersonated_primary_role ?? null,
      startedAt: response.started_at,
    };
  } catch {
    // Se falhar, retorna não impersonando
    return { isImpersonating: false };
  }
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Verifica se está em modo impersonate (local)
 */
export function isImpersonating(): boolean {
  const data = localStorage.getItem(STORAGE_KEYS.IMPERSONATE_DATA);
  if (!data) return false;

  try {
    const parsed: ImpersonateInfo = JSON.parse(data);
    return parsed.isImpersonating === true;
  } catch {
    return false;
  }
}

/**
 * Obtém dados do impersonate do localStorage
 */
export function getStoredImpersonateData(): ImpersonateInfo | null {
  const data = localStorage.getItem(STORAGE_KEYS.IMPERSONATE_DATA);
  if (!data) return null;

  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Limpa todos os dados de impersonate
 * Usado em caso de erro ou logout forçado
 */
export function clearImpersonateData(): void {
  // Restaurar token original se existir
  const originalToken = localStorage.getItem(STORAGE_KEYS.ORIGINAL_TOKEN);
  const originalRefreshToken = localStorage.getItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN);

  if (originalToken) {
    localStorage.setItem('tmf_token', originalToken);
  }
  if (originalRefreshToken) {
    localStorage.setItem('tmf_refresh_token', originalRefreshToken);
  }

  // Limpar dados de impersonate
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
  localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_DATA);
}

// ==========================================
// EXPORT
// ==========================================

export const impersonateApi = {
  startImpersonate,
  exitImpersonate,
  getImpersonateInfo,
  isImpersonating,
  getStoredImpersonateData,
  clearImpersonateData,
};

export default impersonateApi;
